// ============================================================================
// Règles des SKILLCARDS : le schéma (données de la table `card_rules`).
// ============================================================================
// Séparé de rules.ts (instanciation, vérification par le moteur) pour que le
// miroir Deno (supabase/functions/_shared/scratch-model-schema.ts) puisse
// vérifier sa parité sans tirer le moteur. Cf. rules.ts pour le sens de chaque genre.
// ============================================================================

import { z } from 'zod';
import { ScratchModelSchema } from './model';

export const CARD_RULE_KINDS = ['formula', 'rewrite', 'split', 'schema', 'kernel'] as const;
export type CardRuleKind = (typeof CARD_RULE_KINDS)[number];

/**
 * Outils du moteur qu'une règle peut activer, par genre. `core` : réécriture
 * portée par le cœur du moteur (développer, réduire), sans outil à activer.
 */
export const ENGINE_SKILLS_BY_KIND: Record<CardRuleKind, readonly string[]> = {
  formula: ['pythagore', 'baisse', 'secondDegre'],
  rewrite: ['facteurCommun', 'differenceCarres', 'carreParfait', 'core'],
  split: ['produitNul'],
  schema: ['recurrence'],
  // moteur v18 : la règle désigne des règles des paquets du noyau (`kernel_rules`)
  kernel: [],
};
/** Identifiant qualifié d'une règle du noyau : « inequality.divide_negative@1 ». */
export const KERNEL_RULE_ID_RE = /^[a-z][a-z0-9_]{0,39}(\.[a-z][a-z0-9_]{0,39}){1,3}@[1-9]\d{0,2}$/;
const ENGINE_SKILLS = [
  'pythagore',
  'baisse',
  'secondDegre',
  'facteurCommun',
  'differenceCarres',
  'carreParfait',
  'core',
  'produitNul',
  'recurrence',
] as const;

const name = z.string().min(1).max(40);
const lineTextSchema = z.string().min(1).max(400);

export const CardRuleTrapSchema = z.object({
  /** la ligne fausse, écrite avec les rôles de la règle : « A = 2 × (L + l) » */
  line: lineTextSchema,
  title: z.string().min(1).max(120),
  why: z.string().min(1).max(600),
  misconception_id: z.string().min(1).max(80).optional(),
});
export type CardRuleTrap = z.infer<typeof CardRuleTrapSchema>;

export const CardRuleExampleSchema = z.object({
  /** formule générique : valeurs des rôles connus */
  given: z.record(name, z.number()).optional(),
  /** formule générique : LE rôle cherché et sa valeur */
  find: z.record(name, z.number()).optional(),
  /** outil du moteur : une question complète qui l'utilise */
  model: ScratchModelSchema.optional(),
  /** outil du moteur : rôle → grandeur du modèle, pour y rejouer les pièges de la règle */
  bind: z.record(name, name).optional(),
});
export type CardRuleExample = z.infer<typeof CardRuleExampleSchema>;

export const CardRuleSchema = z
  .object({
    /** identifiant stable dans la carte : « aire-rectangle » */
    rule_key: z.string().regex(/^[a-z0-9][a-z0-9-]{1,58}$/),
    kind: z.enum(CARD_RULE_KINDS),
    /** étiquette affichée sur la ligne validée : « Aire du rectangle » */
    label: z.string().min(1).max(60),
    /** « pourquoi », affiché à la demande */
    why: z.string().min(1).max(600),
    /** coup de pouce quand l'élève bloque */
    hint: z.string().max(600).optional(),
    /** HORA gagnés quand la règle justifie une ligne */
    hora: z.number().int().min(1).max(6),
    /** l'égalité, avec des rôles (une lettre ou un mot court par grandeur) */
    formula: lineTextSchema.optional(),
    /** sens de chaque rôle : { L: 'longueur', l: 'largeur', A: 'aire' } */
    roles: z.record(name, z.string().min(1).max(120)).optional(),
    /** conditions d'emploi, en clair : « le triangle est rectangle » */
    conditions: z.array(z.string().min(1).max(200)).max(4).optional(),
    /** outil du moteur qui porte la règle (tous les genres sauf la formule générique) */
    engine_skill: z.enum(ENGINE_SKILLS).optional(),
    /** moteur v18 : règles des paquets du noyau que la carte enseigne (genre `kernel`) */
    kernel_rules: z.array(z.string().regex(KERNEL_RULE_ID_RE)).min(1).max(12).optional(),
    traps: z.array(CardRuleTrapSchema).max(8).optional(),
    examples: z.array(CardRuleExampleSchema).min(1).max(6),
  })
  .superRefine((r, ctx) => {
    const add = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: 'custom', path, message });
    if (r.engine_skill && !ENGINE_SKILLS_BY_KIND[r.kind].includes(r.engine_skill))
      add(['engine_skill'], `l’outil ${r.engine_skill} ne porte pas une règle de genre ${r.kind}`);
    const generic = r.kind === 'formula' && !r.engine_skill;
    if (r.kind === 'kernel') {
      if (!r.kernel_rules?.length)
        add(['kernel_rules'], 'une règle du noyau désigne au moins une règle de ses paquets');
      if (r.engine_skill || r.formula)
        add(['engine_skill'], 'une règle du noyau n’active ni outil ni formule');
    } else if (r.kernel_rules) add(['kernel_rules'], 'réservé aux règles du noyau');
    if (r.kind !== 'formula' && r.kind !== 'kernel' && !r.engine_skill)
      add(['engine_skill'], `une règle ${r.kind} active un outil du moteur`);
    if (generic && (!r.formula || !r.roles))
      add(['formula'], 'une formule générique a une égalité et ses rôles');
    if ((r.traps || []).length && !r.roles)
      add(['roles'], 'des pièges écrits avec des rôles demandent les rôles');
    r.examples.forEach((e, i) => {
      if (generic) {
        if (!e.given || !e.find || Object.keys(e.find).length !== 1)
          add(
            ['examples', i],
            'exemple de formule : des rôles connus (given) et UN rôle cherché (find)',
          );
      } else if (!e.model)
        add(['examples', i], 'exemple d’un outil du moteur : une question complète (model)');
    });
  });
export type CardRule = z.infer<typeof CardRuleSchema>;

/** Les outils du moteur qu’une règle peut activer (pour les prompts et l’éditeur). */
export const RULE_ENGINE_SKILLS: readonly string[] = ENGINE_SKILLS;
